import React, { useState, useEffect } from "react";
import { get, post } from "../../../services/api";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { toast } from "react-toastify";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

function HRMSConfiguration() {
  const token = localStorage.getItem("token");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");
  const organizationId = localStorage.getItem("organizationId");

  const [configData, setConfigData] = useState({
    departments: [],
    categories: [],
    subcategories: [],
    positions: [],
    designations: [],
    contract_types: [],
    employee_statuses: [],
    qualifications: [],
    leave_types: [],
    loan_types: [],
    tax_slabs: [],
    salary_structures: [],
    pay_elements: [],
  });

  const [formInputs, setFormInputs] = useState({});
  const [expanded, setExpanded] = useState({});
  const [pages, setPages] = useState({});
  const [loading, setLoading] = useState(false);
  const itemsPerPage = 5;

  const configSections = [
    {
      key: "departments",
      endpoint: "departments",
      icon: "🏢",
      title: "Departments",
      fields: ["name", "code", "description"],
    },
    {
      key: "categories",
      endpoint: "categories",
      icon: "🏷️",
      title: "Employee Categories",
      fields: ["name", "code", "description"],
    },
    {
      key: "subcategories",
      endpoint: "subcategories",
      icon: "🧷",
      title: "Sub Categories",
      fields: ["name", "code", "description", "categoryId"],
      requiresCategoryId: true,
    },
    {
      key: "positions",
      endpoint: "positions",
      icon: "📌",
      title: "Positions",
      fields: ["name", "code", "description"],
    },
    {
      key: "designations",
      endpoint: "designations",
      icon: "🪪",
      title: "Designations",
      fields: ["name", "code", "description"],
    },
    {
      key: "contract_types",
      endpoint: "contract-types",
      icon: "📄",
      title: "Contract Types",
      fields: ["name", "code", "description"],
    },
    {
      key: "employee_statuses",
      endpoint: "employee-statuses",
      icon: "✅",
      title: "Employee Status",
      fields: ["name", "code", "description"],
    },
    {
      key: "qualifications",
      endpoint: "qualifications",
      icon: "🎓",
      title: "Qualifications",
      fields: ["name", "code", "description"],
    },
    {
      key: "leave_types",
      endpoint: "leave-types",
      icon: "🌴",
      title: "Leave Types",
      fields: ["name", "code", "description"],
    },
    {
      key: "loan_types",
      endpoint: "loan-types",
      icon: "💳",
      title: "Loan Types",
      fields: ["name", "code", "description"],
    },
    {
      key: "tax_slabs",
      endpoint: "taxslabs",
      icon: "🇵🇰",
      title: "Pakistan Tax Slabs",
      fields: [
        "taxYear",
        "fromAmount",
        "toAmount",
        "ratePercent",
        "fixedTax",
        "remarks",
      ],
      isTaxSlab: true,
    },
    {
      key: "salary_structures",
      endpoint: "salary-structures",
      icon: "💰",
      title: "Salary Structures",
      fields: ["name", "code", "description"],
    },
    {
      key: "pay_elements",
      endpoint: "pay-elements",
      icon: "📊",
      title: "Pay Elements (Allowances & Deductions)",
      fields: ["name", "code", "elementType", "isTaxable"],
      isPayElement: true,
    },
  ];

  const loadConfigData = async () => {
    setLoading(true);
    try {
      // Build base query params
      let baseQuery = `?organizationId=${organizationId}`;
      if (savedCenterId) {
        baseQuery += `&centerId=${savedCenterId}`;
      }

      const [
        depts,
        categories,
        subcategories,
        positions,
        designations,
        contractTypes,
        employeeStatuses,
        qualifications,
        leaveTypes,
        loanTypes,
        taxSlabs,
        salaryStructures,
        payElements,
      ] = await Promise.all([
        get(`Configuration/departments${baseQuery}`),
        get(`Configuration/categories${baseQuery}`),
        get(`Configuration/subcategories${baseQuery}`),
        get(`Configuration/positions${baseQuery}`),
        get(`Configuration/designations${baseQuery}`),
        get(`Configuration/contract-types${baseQuery}`),
        get(`Configuration/employee-statuses${baseQuery}`),
        get(`Configuration/qualifications${baseQuery}`),
        get(`Configuration/leave-types${baseQuery}`),
        get(`Configuration/loan-types${baseQuery}`),
        get(`Configuration/taxslabs${baseQuery}`),
        get(`Configuration/salary-structures${baseQuery}`),
        get(`Configuration/pay-elements${baseQuery}`),
      ]);

      setConfigData({
        departments: depts.data || [],
        categories: categories.data || [],
        subcategories: subcategories.data || [],
        positions: positions.data || [],
        designations: designations.data || [],
        contract_types: contractTypes.data || [],
        employee_statuses: employeeStatuses.data || [],
        qualifications: qualifications.data || [],
        leave_types: leaveTypes.data || [],
        loan_types: loanTypes.data || [],
        tax_slabs: taxSlabs.data || [],
        salary_structures: salaryStructures.data || [],
        pay_elements: payElements.data || [],
      });
    } catch (error) {
      console.error("Error loading config:", error);
      toast.error("Failed to load configuration data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigData();
  }, []);

  const handleAccordionChange = (key) => (event, isExpanded) => {
    setExpanded({ ...expanded, [key]: isExpanded });
    if (isExpanded && !pages[key]) {
      setPages({ ...pages, [key]: 1 });
    }
  };

  const handleExpandAll = () => {
    const allExpanded = {};
    const allPages = {};
    configSections.forEach((section) => {
      allExpanded[section.key] = true;
      allPages[section.key] = 1;
    });
    setExpanded(allExpanded);
    setPages(allPages);
  };

  const handleCollapseAll = () => {
    setExpanded({});
  };

  const handleInputChange = (sectionKey, field, value) => {
    setFormInputs({
      ...formInputs,
      [sectionKey]: {
        ...formInputs[sectionKey],
        [field]: value,
      },
    });
  };

  const handleSaveRow = async (section) => {
    const inputs = formInputs[section.key] || {};

    // Validation
    if (section.isTaxSlab) {
      if (!inputs.fromAmount || !inputs.toAmount) {
        toast.error("From Amount and To Amount are required");
        return;
      }
    } else if (section.isPayElement) {
      if (!inputs.name || !inputs.elementType) {
        toast.error("Name and Element Type are required");
        return;
      }
    } else {
      if (!inputs.name) {
        toast.error("Name is required");
        return;
      }
    }

    try {
      // Build endpoint with query parameters
      let endpoint = `Configuration/${section.endpoint}?organizationId=${organizationId}`;
      if (savedCenterId) {
        endpoint += `&centerId=${savedCenterId}`;
      }

      let payload = {};

      if (section.isTaxSlab) {
        // Tax Slab
        payload = {
          taxYear: inputs.taxYear || "2025-26",
          fromAmount: parseFloat(inputs.fromAmount) || 0,
          toAmount: parseFloat(inputs.toAmount) || 0,
          ratePercent: inputs.ratePercent
            ? parseFloat(inputs.ratePercent)
            : null,
          fixedTax: inputs.fixedTax ? parseFloat(inputs.fixedTax) : null,
          remarks: inputs.remarks || "",
          organizationId: parseInt(organizationId),
          ...(savedCenterId && { centerId: parseInt(savedCenterId) }),
        };
      } else if (section.isPayElement) {
        // Pay Element
        payload = {
          name: inputs.name,
          code: inputs.code || "",
          elementType: inputs.elementType,
          isTaxable: inputs.isTaxable === "true" || inputs.isTaxable === true,
          organizationId: parseInt(organizationId),
          ...(savedCenterId && { centerId: parseInt(savedCenterId) }),
        };
      } else {
        // Standard configuration item
        payload = {
          name: inputs.name,
          code: inputs.code || "",
          description: inputs.description || "",
          organizationId: parseInt(organizationId),
          ...(savedCenterId && { centerId: parseInt(savedCenterId) }),
        };

        // Add categoryId for subcategories
        if (section.requiresCategoryId) {
          if (!inputs.categoryId) {
            toast.error("Category is required for Sub Category");
            return;
          }
          // Add categoryId to query parameters for subcategories
          endpoint += `&categoryId=${inputs.categoryId}`;
          // Also add to payload if needed
          payload.categoryId = parseInt(inputs.categoryId);
        }
      }

      const response = await post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.success) {
        toast.success(response.message || "Saved successfully");
        // Refresh the data
        await loadConfigData();
        // Clear inputs
        setFormInputs({ ...formInputs, [section.key]: {} });
      }
    } catch (error) {
      console.error("Error saving:", error);
      toast.error(error.response?.data?.message || "Failed to save");
    }
  };

  const handleClearInputs = (sectionKey) => {
    setFormInputs({
      ...formInputs,
      [sectionKey]: {},
    });
  };

  const paginateData = (data, page) => {
    const start = (page - 1) * itemsPerPage;
    return data.slice(start, start + itemsPerPage);
  };

  const renderStandardForm = (section) => {
    const inputs = formInputs[section.key] || {};
    const currentPage = pages[section.key] || 1;

    return (
      <div>
        {/* Entry Form */}
        <div
          className="border rounded p-3 mb-3"
          style={{ background: "#f9fafb" }}
        >
          <h6 className="mb-3">Add New Entry</h6>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">
                Name<span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                placeholder="Enter name"
                value={inputs.name || ""}
                onChange={(e) =>
                  handleInputChange(section.key, "name", e.target.value)
                }
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Code</label>
              <input
                className="form-control"
                placeholder="Enter code"
                value={inputs.code || ""}
                onChange={(e) =>
                  handleInputChange(section.key, "code", e.target.value)
                }
              />
            </div>
            <div className="col-md-5">
              <label className="form-label">Description</label>
              <input
                className="form-control"
                placeholder="Enter description"
                value={inputs.description || ""}
                onChange={(e) =>
                  handleInputChange(section.key, "description", e.target.value)
                }
              />
            </div>

            {/* Category Dropdown for Sub-Categories */}
            {section.requiresCategoryId && (
              <div className="col-md-12">
                <label className="form-label">
                  Parent Category<span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={inputs.categoryId || ""}
                  onChange={(e) =>
                    handleInputChange(section.key, "categoryId", e.target.value)
                  }
                >
                  <option value="">-- Select Category --</option>
                  {(configData.categories || []).map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="mt-3 d-flex gap-2">
            <button
              className="btn btn-success btn-sm"
              onClick={() => handleSaveRow(section)}
            >
              <i className="fas fa-check me-1"></i>
              Save
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handleClearInputs(section.key)}
            >
              <i className="fas fa-redo me-1"></i>
              Clear
            </button>
          </div>
        </div>

        {/* Data Table */}
        <div>
          <h6 className="mb-2">Saved Entries</h6>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f3f4f6" }}>
                  <TableCell width={40}>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell width={120}>Code</TableCell>
                  <TableCell>Description</TableCell>
                  {section.requiresCategoryId && (
                    <TableCell>Category</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginateData(configData[section.key] || [], currentPage).map(
                  (row, index) => (
                    <TableRow key={row.id || index} hover>
                      <TableCell>
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.code}</TableCell>
                      <TableCell>{row.description}</TableCell>
                      {section.requiresCategoryId && (
                        <TableCell>
                          {configData.categories.find(
                            (c) => c.id === row.categoryId,
                          )?.name || "-"}
                        </TableCell>
                      )}
                    </TableRow>
                  ),
                )}
                {(configData[section.key] || []).length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={section.requiresCategoryId ? 5 : 4}
                      align="center"
                      sx={{ color: "text.secondary", py: 3 }}
                    >
                      No entries yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {(configData[section.key] || []).length > itemsPerPage && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination
                count={Math.ceil(
                  (configData[section.key] || []).length / itemsPerPage,
                )}
                page={currentPage}
                onChange={(e, page) =>
                  setPages({ ...pages, [section.key]: page })
                }
                color="primary"
                size="small"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTaxSlabForm = (section) => {
    const inputs = formInputs[section.key] || {};
    const currentPage = pages[section.key] || 1;

    return (
      <div>
        {/* Entry Form */}
        <div
          className="border rounded p-3 mb-3"
          style={{ background: "#f9fafb" }}
        >
          <h6 className="mb-3">Add Tax Slab</h6>
          <div className="row g-3">
            <div className="col-md-2">
              <label className="form-label">Tax Year</label>
              <input
                className="form-control"
                placeholder="2025-26"
                value={inputs.taxYear || "2025-26"}
                onChange={(e) =>
                  handleInputChange(section.key, "taxYear", e.target.value)
                }
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">
                From Amount<span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="0"
                value={inputs.fromAmount || ""}
                onChange={(e) =>
                  handleInputChange(section.key, "fromAmount", e.target.value)
                }
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">
                To Amount<span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="0"
                value={inputs.toAmount || ""}
                onChange={(e) =>
                  handleInputChange(section.key, "toAmount", e.target.value)
                }
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Rate %</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                placeholder="0"
                value={inputs.ratePercent || ""}
                onChange={(e) =>
                  handleInputChange(section.key, "ratePercent", e.target.value)
                }
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Fixed Tax</label>
              <input
                type="number"
                className="form-control"
                placeholder="0"
                value={inputs.fixedTax || ""}
                onChange={(e) =>
                  handleInputChange(section.key, "fixedTax", e.target.value)
                }
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Remarks</label>
              <input
                className="form-control"
                placeholder="Optional"
                value={inputs.remarks || ""}
                onChange={(e) =>
                  handleInputChange(section.key, "remarks", e.target.value)
                }
              />
            </div>
          </div>
          <div className="mt-3 d-flex gap-2">
            <button
              className="btn btn-success btn-sm"
              onClick={() => handleSaveRow(section)}
            >
              <i className="fas fa-check me-1"></i>
              Save
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handleClearInputs(section.key)}
            >
              <i className="fas fa-redo me-1"></i>
              Clear
            </button>
          </div>
        </div>

        {/* Data Table */}
        <div>
          <h6 className="mb-2">Saved Slabs</h6>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f3f4f6" }}>
                  <TableCell width={40}>#</TableCell>
                  <TableCell>Tax Year</TableCell>
                  <TableCell align="right">From</TableCell>
                  <TableCell align="right">To</TableCell>
                  <TableCell align="right">Rate %</TableCell>
                  <TableCell align="right">Fixed</TableCell>
                  <TableCell>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginateData(configData[section.key] || [], currentPage).map(
                  (row, index) => (
                    <TableRow key={row.id || index} hover>
                      <TableCell>
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </TableCell>
                      <TableCell>{row.taxYear}</TableCell>
                      <TableCell align="right">
                        {row.fromAmount?.toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        {row.toAmount?.toLocaleString()}
                      </TableCell>
                      <TableCell align="right">{row.ratePercent}%</TableCell>
                      <TableCell align="right">
                        {row.fixedTax?.toLocaleString()}
                      </TableCell>
                      <TableCell>{row.remarks}</TableCell>
                    </TableRow>
                  ),
                )}
                {(configData[section.key] || []).length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{ color: "text.secondary", py: 3 }}
                    >
                      No slabs configured yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {(configData[section.key] || []).length > itemsPerPage && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination
                count={Math.ceil(
                  (configData[section.key] || []).length / itemsPerPage,
                )}
                page={currentPage}
                onChange={(e, page) =>
                  setPages({ ...pages, [section.key]: page })
                }
                color="primary"
                size="small"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPayElementForm = (section) => {
    const inputs = formInputs[section.key] || {};
    const currentPage = pages[section.key] || 1;

    return (
      <div>
        {/* Entry Form */}
        <div
          className="border rounded p-3 mb-3"
          style={{ background: "#f9fafb" }}
        >
          <h6 className="mb-3">Add Pay Element</h6>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">
                Name<span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                placeholder="e.g. House Rent Allowance"
                value={inputs.name || ""}
                onChange={(e) =>
                  handleInputChange(section.key, "name", e.target.value)
                }
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Code</label>
              <input
                className="form-control"
                placeholder="e.g. HRA"
                value={inputs.code || ""}
                onChange={(e) =>
                  handleInputChange(section.key, "code", e.target.value)
                }
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">
                Type<span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={inputs.elementType || ""}
                onChange={(e) =>
                  handleInputChange(section.key, "elementType", e.target.value)
                }
              >
                <option value="">-- Select Type --</option>
                <option value="Benefit">Benefit (Allowance)</option>
                <option value="Deduction">Deduction</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Taxable?</label>
              <select
                className="form-select"
                value={inputs.isTaxable || "false"}
                onChange={(e) =>
                  handleInputChange(section.key, "isTaxable", e.target.value)
                }
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
          </div>
          <div className="mt-3 d-flex gap-2">
            <button
              className="btn btn-success btn-sm"
              onClick={() => handleSaveRow(section)}
            >
              <i className="fas fa-check me-1"></i>
              Save
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handleClearInputs(section.key)}
            >
              <i className="fas fa-redo me-1"></i>
              Clear
            </button>
          </div>
        </div>

        {/* Data Table */}
        <div>
          <h6 className="mb-2">Saved Pay Elements</h6>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f3f4f6" }}>
                  <TableCell width={40}>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell width={100}>Code</TableCell>
                  <TableCell width={120}>Type</TableCell>
                  <TableCell width={100}>Taxable</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginateData(configData[section.key] || [], currentPage).map(
                  (row, index) => (
                    <TableRow key={row.id || index} hover>
                      <TableCell>
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.code}</TableCell>
                      <TableCell>
                        <span
                          className={`badge ${
                            row.elementType === "Benefit"
                              ? "bg-success"
                              : "bg-warning"
                          }`}
                        >
                          {row.elementType}
                        </span>
                      </TableCell>
                      <TableCell>{row.isTaxable ? "Yes" : "No"}</TableCell>
                    </TableRow>
                  ),
                )}
                {(configData[section.key] || []).length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{ color: "text.secondary", py: 3 }}
                    >
                      No pay elements configured yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {(configData[section.key] || []).length > itemsPerPage && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination
                count={Math.ceil(
                  (configData[section.key] || []).length / itemsPerPage,
                )}
                page={currentPage}
                onChange={(e, page) =>
                  setPages({ ...pages, [section.key]: page })
                }
                color="primary"
                size="small"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="row g-3">
      <div className="col-md-12">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4>HRMS Configuration</h4>
            <p className="text-muted small mb-0">
              These configurations power dropdown lists across Employee Profile,
              Onboarding and Payroll forms.
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={handleExpandAll}
            >
              <i className="fas fa-expand me-1"></i>
              Expand All
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={handleCollapseAll}
            >
              <i className="fas fa-compress me-1"></i>
              Collapse All
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={loadConfigData}
              disabled={loading}
            >
              <i className="fas fa-sync me-1"></i>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {!loading && (
          <div className="card">
            <div className="card-body">
              {configSections.map((section) => (
                <Accordion
                  key={section.key}
                  expanded={expanded[section.key] || false}
                  onChange={handleAccordionChange(section.key)}
                  sx={{ mb: 2, boxShadow: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <div className="d-flex align-items-center gap-2">
                      <span style={{ fontSize: "1.5rem" }}>{section.icon}</span>
                      <div>
                        <div style={{ fontWeight: 600 }}>{section.title}</div>
                        <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                          {(configData[section.key] || []).length} entries
                        </div>
                      </div>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    {section.isTaxSlab
                      ? renderTaxSlabForm(section)
                      : section.isPayElement
                        ? renderPayElementForm(section)
                        : renderStandardForm(section)}
                  </AccordionDetails>
                </Accordion>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HRMSConfiguration;
